/** LEP-00001 — Enterprise Integration Platform framework (mirrors pkg/enterpriseintegration) */

export const LEP_EI_ID = 'LEP-00001';

export interface EventBusKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface CatalogEntryRow {
  feature: string;
  name: string;
  count: number;
  status: string;
}

export interface OrchestrationRow {
  capability: string;
  name: string;
  status: string;
  steps: number;
}

export interface MessageQueueRow {
  feature: string;
  name: string;
  depth: number;
  status: string;
}

export interface APIWebhookRow {
  feature: string;
  name: string;
  calls: string;
  status: string;
}

export interface AIOrchestrationRow {
  capability: string;
  model: string;
  requests: string;
  confidence: string;
}

export interface ObservabilityRow {
  signal: string;
  volume: string;
  status: string;
}

export interface WiringCheckRow {
  feature: string;
  name: string;
  status: string;
  score: number;
}

export const EVENT_BUS_CAPABILITIES = [
  'Business Events', 'Domain Events', 'Integration Events', 'Versioning',
  'Schemas', 'Validation', 'Retention', 'Priority',
];

export const EVENT_CATALOG_FEATURES = [
  'Event Definitions', 'Producers', 'Consumers', 'Payload Schemas',
  'Compatibility Rules', 'Lifecycle Management',
];

export const ORCHESTRATION_CAPABILITIES = [
  'Business Workflows', 'Cross-module Transactions', 'Saga Execution',
  'Compensation Logic', 'Failure Recovery',
];

export const MESSAGE_INFRA_FEATURES = [
  'Priority Queues', 'Retry Queues', 'Dead Letter Queues', 'Delayed Jobs',
  'Distributed Scheduling', 'Distributed Locking',
];

export const API_WEBHOOK_FEATURES = [
  'REST APIs', 'Webhook Gateway', 'Authentication', 'Signing',
  'Rate Limiting', 'Versioning', 'Monitoring', 'Replay',
];

export const AI_ORCHESTRATION_CAPABILITIES = [
  'Model Routing', 'Prompt Assembly', 'Context Injection', 'Fallback Policies',
  'Response Validation', 'Cost Tracking', 'Confidence Scoring',
];

export const OBSERVABILITY_SIGNALS = [
  'Logs', 'Metrics', 'Distributed Traces', 'Business Events',
  'Marketplace Events', 'AI Events', 'User Events',
];

export const PRODUCTION_WIRING_FEATURES = [
  'File Processing', 'Redis Caching', 'Search Indexing', 'Vector Search',
  'Disaster Recovery', 'Security Controls',
];

export const INTEGRATION_ANALYTICS = [
  'Latency', 'Throughput', 'Availability', 'Error Rates',
  'Queue Health', 'Worker Utilization', 'Integration Cost',
];

export const SECURITY_CONTROLS = [
  'OAuth', 'JWT', 'API Signing', 'Secret Management',
  'Encryption', 'Replay Protection', 'Certificate Rotation',
];

export const FILE_FORMATS = [
  'CSV', 'Excel', 'Images', 'Videos', 'PDF', 'ZIP', 'Virus Scanning', 'Validation', 'Versioning',
];

export const SEARCH_CAPABILITIES = [
  'Redis Caching', 'Search Indexing', 'Vector Search', 'Hybrid Search', 'Marketplace Search', 'Knowledge Search',
];

export const DISASTER_RECOVERY = [
  'Replay', 'Checkpointing', 'Snapshots', 'Queue Recovery', 'Workflow Recovery',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Enterprise Event Bus', 'Event Catalog', 'Service Orchestration', 'Message Infrastructure',
  'API & Webhook Platform', 'AI Orchestration', 'Observability', 'Production Wiring',
];

export const LIVE_ROUTES: Record<string, string> = {
  integrations: '/integrations',
  integrationGovernance: '/platform/integration-governance',
  workflows: '/workflows',
  observability: '/observability',
  sre: '/sre',
};

export const DEMO_EVENT_BUS_KPIS: EventBusKPI[] = [
  { label: 'Events/sec', value: '12.4K', change: '+8%', positive: true },
  { label: 'Business Events', value: '842K/day', change: '+12%', positive: true },
  { label: 'Domain Events', value: '1.2M/day', change: '+6%', positive: true },
  { label: 'Schema Valid', value: '99.8%', change: '+0.2%', positive: true },
  { label: 'Avg Latency', value: '18ms', change: '-3ms', positive: true },
  { label: 'DLQ Depth', value: '12', change: '-8', positive: true },
  { label: 'Retention', value: '90 days', change: 'compliant', positive: true },
  { label: 'Priority Queues', value: '4 active', change: 'healthy', positive: true },
];

export const DEMO_CATALOG: CatalogEntryRow[] = [
  { feature: 'Event Definitions', name: 'Event Definitions', count: 248, status: 'registered' },
  { feature: 'Producers', name: 'Producers', count: 42, status: 'active' },
  { feature: 'Consumers', name: 'Consumers', count: 86, status: 'active' },
  { feature: 'Payload Schemas', name: 'Payload Schemas', count: 248, status: 'validated' },
  { feature: 'Compatibility Rules', name: 'Compatibility Rules', count: 64, status: 'enforced' },
  { feature: 'Lifecycle Policies', name: 'Lifecycle Policies', count: 12, status: 'governed' },
];

export const DEMO_ORCHESTRATION: OrchestrationRow[] = [
  { capability: 'Business Workflows', name: 'Catalog Publish Pipeline', status: 'running', steps: 8 },
  { capability: 'Saga Execution', name: 'Order Fulfillment Saga', status: 'running', steps: 12 },
  { capability: 'Cross-module Transactions', name: 'Pricing Sync Transaction', status: 'idle', steps: 5 },
  { capability: 'Compensation Logic', name: 'Publish Rollback Handler', status: 'armed', steps: 4 },
  { capability: 'Failure Recovery', name: 'Marketplace Reconnect', status: 'standby', steps: 6 },
];

export const DEMO_QUEUES: MessageQueueRow[] = [
  { feature: 'Priority Queues', name: 'critical-events', depth: 24, status: 'healthy' },
  { feature: 'Retry Queues', name: 'retry-tier-1', depth: 8, status: 'healthy' },
  { feature: 'Dead Letter Queues', name: 'dlq-integration', depth: 12, status: 'review' },
  { feature: 'Delayed Jobs', name: 'scheduled-sync', depth: 42, status: 'healthy' },
  { feature: 'Distributed Scheduling', name: 'cron-dispatcher', depth: 0, status: 'active' },
  { feature: 'Distributed Locking', name: 'saga-coordinator', depth: 3, status: 'active' },
];

export const DEMO_API_WEBHOOKS: APIWebhookRow[] = [
  { feature: 'REST APIs', name: 'Integration REST API', calls: '842K/day', status: 'healthy' },
  { feature: 'Webhook Gateway', name: 'Webhook Gateway', calls: '124K/day', status: 'healthy' },
  { feature: 'Authentication', name: 'OAuth/JWT Auth', calls: '—', status: 'enforced' },
  { feature: 'Signing', name: 'HMAC Signing', calls: '—', status: 'enforced' },
  { feature: 'Rate Limiting', name: 'Rate Limiter', calls: '42 throttled', status: 'active' },
  { feature: 'Monitoring', name: 'API Monitor', calls: '99.9% uptime', status: 'healthy' },
];

export const DEMO_AI_ORCHESTRATION: AIOrchestrationRow[] = [
  { capability: 'Model Routing', model: 'gpt-4o-mini → gpt-4o', requests: '18.4K/day', confidence: '94%' },
  { capability: 'Prompt Assembly', model: 'prompt-orch-v2', requests: '18.4K/day', confidence: '96%' },
  { capability: 'Context Injection', model: 'brand-dna + catalog', requests: '12.2K/day', confidence: '91%' },
  { capability: 'Fallback Policies', model: 'claude-3-haiku', requests: '842/day', confidence: '88%' },
  { capability: 'Cost Tracking', model: 'FinOps tracker', requests: '₹42K/mo', confidence: '—' },
];

export const DEMO_OBSERVABILITY: ObservabilityRow[] = [
  { signal: 'Logs', volume: '2.4M/day', status: 'indexed' },
  { signal: 'Metrics', volume: '842K series', status: 'scraped' },
  { signal: 'Distributed Traces', volume: '124K/day', status: 'sampled' },
  { signal: 'Business Events', volume: '842K/day', status: 'correlated' },
  { signal: 'Marketplace Events', volume: '420K/day', status: 'correlated' },
  { signal: 'AI Events', volume: '18.4K/day', status: 'correlated' },
  { signal: 'User Events', volume: '64K/day', status: 'correlated' },
];

export const DEMO_WIRING: WiringCheckRow[] = [
  { feature: 'File Processing', name: 'File Processing (CSV/PDF/ZIP)', status: 'passed', score: 100 },
  { feature: 'Redis Caching', name: 'Redis Caching', status: 'passed', score: 98 },
  { feature: 'Search Indexing', name: 'Search Indexing', status: 'passed', score: 96 },
  { feature: 'Vector Search', name: 'Vector Search', status: 'passed', score: 94 },
  { feature: 'Disaster Recovery', name: 'Disaster Recovery', status: 'passed', score: 97 },
  { feature: 'Security Controls', name: 'Security Controls', status: 'passed', score: 99 },
];

export function buildIntegrationDashboard() {
  return {
    event_bus_capabilities: EVENT_BUS_CAPABILITIES.length,
    catalog_features: EVENT_CATALOG_FEATURES.length,
    orchestration_capabilities: ORCHESTRATION_CAPABILITIES.length,
    message_infra_features: MESSAGE_INFRA_FEATURES.length,
    observability_signals: OBSERVABILITY_SIGNALS.length,
    wiring_features: PRODUCTION_WIRING_FEATURES.length,
  };
}
